/**
 * PendingEmailBanner - Banner persistente per verifica email pendente
 *
 * Mostra un avviso quando l'utente ha richiesto un cambio email
 * che non è ancora stato confermato via link.
 *
 * Caratteristiche:
 * - Mostra tempo rimanente prima della scadenza (24h grace period)
 * - Possibilità di reinviare l'email di verifica
 * - Dismissible temporaneamente (torna dopo reload)
 *
 * @author Salvo de Luca - Dialogicom s.r.l.s.
 * @version 1.0.0
 */

import React, { useState, useEffect } from 'react';
import {
  AlertTriangle,
  Mail,
  Clock,
  X,
  XCircle,
  RefreshCw,
  Loader2
} from 'lucide-react';
import { useAuth } from './AuthProvider';

interface PendingEmailBannerProps {
  /** Classe CSS aggiuntiva per il contenitore */
  className?: string;
}

export const PendingEmailBanner: React.FC<PendingEmailBannerProps> = ({
  className = ''
}) => {
  const { user, refreshUser } = useAuth();
  const [isDismissed, setIsDismissed] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<string>('');

  // Calcola il tempo rimanente
  useEffect(() => {
    if (!user?.email_change_requested_at) return;

    const calculateTimeRemaining = () => {
      const requestedAt = new Date(user.email_change_requested_at!);
      const expiresAt = new Date(requestedAt.getTime() + 24 * 60 * 60 * 1000);
      const now = new Date();
      const diff = expiresAt.getTime() - now.getTime();

      if (diff <= 0) {
        setTimeRemaining('Scaduto');
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      if (hours > 0) {
        setTimeRemaining(`${hours}h ${minutes}m`);
      } else {
        setTimeRemaining(`${minutes} minuti`);
      }
    };

    calculateTimeRemaining();
    const interval = setInterval(calculateTimeRemaining, 60000); // Aggiorna ogni minuto

    return () => clearInterval(interval);
  }, [user?.email_change_requested_at]);

  // Non mostrare se non c'è un cambio email pendente o se l'utente ha nascosto il banner
  if (!user?.has_pending_email_change || isDismissed) {
    return null;
  }

  // Handler per reinvio email verifica
  const handleResend = async () => {
    if (!user?.pending_email) return;

    setIsResending(true);
    setResendError(null);

    try {
      const response = await fetch('/api/auth/request-email-change', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId: user.id,
          currentEmail: user.email,
          newEmail: user.pending_email,
          password: 'resend-skip' // Backend riconosce questo come reinvio
        })
      });

      const data = await response.json();

      if (data.success) {
        setResendSuccess(true);
        setTimeout(() => setResendSuccess(false), 5000);
        // Refresh user data
        await refreshUser();
      } else {
        setResendError(data.error || 'Errore durante il reinvio');
      }
    } catch (err) {
      setResendError('Errore di connessione');
    } finally {
      setIsResending(false);
    }
  };

  // Handler per annullare il cambio email
  const handleCancel = async () => {
    if (!user?.id) return;

    setIsCancelling(true);
    setCancelError(null);

    try {
      const response = await fetch('/api/auth/cancel-email-change', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userId: user.id })
      });

      const data = await response.json();

      if (data.success) {
        // Refresh user data - il banner sparirà automaticamente
        await refreshUser();
      } else {
        setCancelError(data.error || 'Errore durante l\'annullamento');
      }
    } catch (err) {
      setCancelError('Errore di connessione');
    } finally {
      setIsCancelling(false);
    }
  };

  const isExpired = user.pending_email_grace_period_expired;

  return (
    <div
      className={`w-full px-4 py-3 ${
        isExpired
          ? 'bg-red-500/20 border-b border-red-500/50'
          : 'bg-amber-500/20 border-b border-amber-500/50'
      } ${className}`}
    >
      <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-3">
        {/* Icona + Messaggio */}
        <div className="flex items-center gap-3">
          <div className={`p-1.5 rounded-full ${isExpired ? 'bg-red-500/30' : 'bg-amber-500/30'}`}>
            {isExpired ? (
              <AlertTriangle className="w-5 h-5 text-red-400" />
            ) : (
              <Mail className="w-5 h-5 text-amber-400" />
            )}
          </div>
          <div>
            <p className={`text-sm font-medium ${isExpired ? 'text-red-300' : 'text-amber-300'}`}>
              {isExpired
                ? 'Verifica email scaduta!'
                : 'Verifica email in attesa'}
            </p>
            <p className="text-xs text-gray-300">
              {isExpired ? (
                <>
                  La richiesta di cambio a <strong>{user.pending_email}</strong> è scaduta.
                  Richiedi nuovamente dalla pagina profilo.
                </>
              ) : (
                <>
                  Controlla <strong>{user.pending_email}</strong> e clicca sul link di conferma.
                </>
              )}
            </p>
          </div>
        </div>

        {/* Tempo rimanente + Azioni */}
        <div className="flex items-center gap-3">
          {/* Tempo rimanente */}
          {!isExpired && (
            <div className="flex items-center gap-1.5 px-2 py-1 bg-black/20 rounded text-xs text-gray-300">
              <Clock className="w-3.5 h-3.5" />
              <span>{timeRemaining}</span>
            </div>
          )}

          {/* Pulsante reinvia (solo se non scaduto) */}
          {!isExpired && (
            <button
              onClick={handleResend}
              disabled={isResending || isCancelling}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600/30 hover:bg-amber-600/50 disabled:bg-gray-600/30 text-amber-200 rounded text-xs font-medium transition-colors"
            >
              {isResending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <RefreshCw className="w-3.5 h-3.5" />
              )}
              {isResending ? 'Invio...' : 'Reinvia'}
            </button>
          )}

          {/* Pulsante annulla cambio email */}
          <button
            onClick={handleCancel}
            disabled={isCancelling || isResending}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600/30 hover:bg-red-600/50 disabled:bg-gray-600/30 text-red-200 rounded text-xs font-medium transition-colors"
            title="Annulla la richiesta di cambio email"
          >
            {isCancelling ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <XCircle className="w-3.5 h-3.5" />
            )}
            {isCancelling ? 'Annullamento...' : 'Annulla'}
          </button>

          {/* Pulsante chiudi (dismiss temporaneo) */}
          <button
            onClick={() => setIsDismissed(true)}
            className="p-1 hover:bg-white/10 rounded transition-colors"
            title="Nascondi banner (riappare al prossimo accesso)"
          >
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>
      </div>

      {/* Feedback reinvio */}
      {resendSuccess && (
        <div className="max-w-7xl mx-auto mt-2">
          <p className="text-xs text-green-400">
            Email di verifica reinviata con successo!
          </p>
        </div>
      )}

      {resendError && (
        <div className="max-w-7xl mx-auto mt-2">
          <p className="text-xs text-red-400">
            {resendError}
          </p>
        </div>
      )}

      {cancelError && (
        <div className="max-w-7xl mx-auto mt-2">
          <p className="text-xs text-red-400">
            {cancelError}
          </p>
        </div>
      )}
    </div>
  );
};

export default PendingEmailBanner;
